import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NotificationService } from "@/gen/omnios/v1/notification_connect";
import { formatRelative } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useNotificationStore } from "@/lib/notifications";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const client = createClient(NotificationService, transport);

const PAGE_SIZE = 50;

interface NotificationRow {
	id: string;
	title: string;
	body: string;
	link: string;
	isRead: boolean;
	createdAt: Date;
}

interface NotificationPage {
	items: NotificationRow[];
	nextPageToken: string;
}

export const Route = createFileRoute("/notifications")({
	component: NotificationsPage,
});

function NotificationsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { unreadCount, markRead, markAllRead } = useNotificationStore();
	const [filter, setFilter] = useState<"all" | "unread">("all");

	useBreadcrumbs([{ label: t("notifications") }]);

	const listQuery = useInfiniteQuery({
		queryKey: ["notifications", filter],
		queryFn: async ({ pageParam }): Promise<NotificationPage> => {
			const res = await client.listNotifications({
				page: { pageSize: PAGE_SIZE, pageToken: pageParam ?? "" },
				unreadOnly: filter === "unread",
			});
			return {
				items: res.notifications.map((n) => ({
					id: n.id,
					title: n.title,
					body: n.body,
					link: n.link,
					isRead: n.isRead,
					createdAt: n.createdAt ? n.createdAt.toDate() : new Date(),
				})),
				nextPageToken: res.page?.nextPageToken ?? "",
			};
		},
		initialPageParam: "" as string,
		getNextPageParam: (last) => last.nextPageToken || undefined,
	});

	const items = useMemo(
		() => listQuery.data?.pages.flatMap((p) => p.items) ?? [],
		[listQuery.data],
	);

	// Mark items read in the query cache in place (both filters) so the
	// list does not jump; the zustand store keeps the bell badge in sync.
	const markReadLocal = (ids: string[]) => {
		queryClient.setQueriesData<{ pages: NotificationPage[] }>(
			{ queryKey: ["notifications"] },
			(data) =>
				data && {
					...data,
					pages: data.pages.map((p) => ({
						...p,
						items: p.items.map((n) =>
							ids.includes(n.id) ? { ...n, isRead: true } : n,
						),
					})),
				},
		);
	};

	const handleClick = (n: NotificationRow) => {
		if (!n.isRead) {
			void markRead([n.id]);
			markReadLocal([n.id]);
		}
		if (n.link) navigate({ to: n.link });
	};

	const handleMarkAllRead = async () => {
		await markAllRead();
		queryClient.invalidateQueries({ queryKey: ["notifications"] });
	};

	// Infinite scroll: load the next page when the sentinel becomes visible.
	const sentinelRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;
		const io = new IntersectionObserver(
			(entries) => {
				if (
					entries[0]?.isIntersecting &&
					listQuery.hasNextPage &&
					!listQuery.isFetchingNextPage
				) {
					listQuery.fetchNextPage();
				}
			},
			{ rootMargin: "200px" },
		);
		io.observe(el);
		return () => io.disconnect();
	}, [listQuery]);

	return (
		<div className="h-full overflow-y-auto">
			<div className="mx-auto w-full max-w-2xl px-4 py-6">
				<div className="flex items-center justify-between gap-2 mb-4">
					<h1 className="text-lg font-semibold">{t("notifications")}</h1>
					{unreadCount > 0 && (
						<Button
							variant="outline"
							size="sm"
							className="gap-1.5"
							onClick={handleMarkAllRead}
						>
							<CheckCheck className="h-3.5 w-3.5" />
							{t("mark_all_read")}
						</Button>
					)}
				</div>

				<Tabs
					value={filter}
					onValueChange={(v) => setFilter(v as "all" | "unread")}
					className="mb-3"
				>
					<TabsList>
						<TabsTrigger value="all">{t("all")}</TabsTrigger>
						<TabsTrigger value="unread">
							{t("unread")}
							{unreadCount > 0 && (
								<span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary/10 text-primary px-1 text-[10px]">
									{unreadCount}
								</span>
							)}
						</TabsTrigger>
					</TabsList>
				</Tabs>

				{listQuery.isLoading ? (
					<div className="flex justify-center py-12">
						<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : items.length === 0 ? (
					<div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
						<Bell className="h-8 w-8 opacity-40" />
						<p className="text-sm">
							{filter === "unread"
								? t("no_unread_notifications")
								: t("no_notifications")}
						</p>
					</div>
				) : (
					<div className="rounded-lg border divide-y">
						{items.map((n) => (
							<Button
								key={n.id}
								type="button"
								variant="ghost"
								className={cn(
									"flex w-full gap-3 px-4 py-3 text-left h-auto rounded-none justify-start first:rounded-t-lg last:rounded-b-lg",
									!n.isRead && "bg-primary/5",
								)}
								onClick={() => handleClick(n)}
							>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">{n.title}</p>
									{n.body && (
										<p className="text-xs text-muted-foreground truncate">
											{n.body}
										</p>
									)}
									<p className="text-[10px] text-muted-foreground/60 mt-0.5">
										{formatRelative(n.createdAt)}
									</p>
								</div>
								{!n.isRead && (
									<div className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
								)}
							</Button>
						))}
					</div>
				)}

				<div ref={sentinelRef} />
				{listQuery.isFetchingNextPage && (
					<div className="flex justify-center py-3">
						<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
					</div>
				)}
			</div>
		</div>
	);
}
